import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { richTextToPlainText } from 'src/shared/lib/richText';
import {
  buildRichTextImageHtml,
  canInsertRichTextHtml,
  imageFileToEmbeddedHtml,
  isSupportedRichTextImageType,
} from 'src/shared/lib/richTextImages';

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeightClassName?: string;
  maxLength?: number;
  onError?: (message: string) => void;
};

type ToolbarButtonProps = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

const ToolbarButton: FC<ToolbarButtonProps> = ({ label, onClick, disabled }) => (
  <button
    type="button"
    className="rounded border border-violet-400 px-2 py-1 text-extra-xs text-black disabled:cursor-default disabled:opacity-50 dark:border-violet-900 dark:text-white"
    onMouseDown={event => {
      event.preventDefault();
      onClick();
    }}
    disabled={disabled}
  >
    {label}
  </button>
);

const withCssStyles = () => {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    return;
  }

  document.execCommand('styleWithCSS', false, 'true');
};

export const RichTextEditor: FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Введите комментарий...',
  disabled = false,
  minHeightClassName = 'min-h-28',
  maxLength,
  onError,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isEmbeddingImage, setIsEmbeddingImage] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }

    if (editor.innerHTML !== value) {
      editor.innerHTML = value || '';
    }
  }, [value]);

  const plainText = useMemo(() => richTextToPlainText(value), [value]);
  const isBusy = disabled || isEmbeddingImage;

  const emitChange = () => {
    onChange(editorRef.current?.innerHTML || '');
  };

  const reportError = (message: string) => {
    onError?.(message);
  };

  const applyCommand = (command: string, commandValue?: string) => {
    if (isBusy) {
      return;
    }

    editorRef.current?.focus();
    withCssStyles();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const insertHtml = (html: string): boolean => {
    if (isBusy || !editorRef.current) {
      return false;
    }

    const currentHtml = editorRef.current.innerHTML || '';
    if (!canInsertRichTextHtml(currentHtml, html, maxLength)) {
      reportError(
        'Комментарий превышает допустимый размер. Уменьшите вложение или вставьте ссылку на изображение.',
      );
      return false;
    }

    editorRef.current.focus();
    withCssStyles();
    document.execCommand('insertHTML', false, html);
    emitChange();
    return true;
  };

  const handleCreateLink = () => {
    const url = window.prompt('Введите ссылку');
    if (!url) {
      return;
    }
    applyCommand('createLink', url.trim());
  };

  const handleInsertImageByUrl = () => {
    const url = window.prompt('Введите URL изображения');
    if (!url) {
      return;
    }
    insertHtml(buildRichTextImageHtml(url.trim()));
  };

  const handleInsertImageFile = async (file: File) => {
    if (isBusy) {
      return;
    }

    if (!isSupportedRichTextImageType(file.type)) {
      reportError('Поддерживаются только изображения.');
      return;
    }

    setIsEmbeddingImage(true);

    try {
      const currentHtml = editorRef.current?.innerHTML || '';
      const remainingLength =
        typeof maxLength === 'number' && maxLength > 0
          ? Math.max(maxLength - currentHtml.length, 0)
          : undefined;
      const html = await imageFileToEmbeddedHtml(file, { maxLength: remainingLength });
      insertHtml(html);
    } catch (error: any) {
      const message = (error as Error)?.message || 'Не удалось вставить изображение';

      if (message === 'Image file is too large') {
        reportError('Изображение слишком большое. Используйте файл до 8 МБ.');
        return;
      }

      if (message === 'Image is too large to embed into the comment') {
        reportError(
          'Изображение не помещается в комментарий даже после сжатия. Уменьшите размер или вставьте ссылку на изображение.',
        );
        return;
      }

      reportError('Не удалось подготовить изображение для комментария.');
    } finally {
      setIsEmbeddingImage(false);
    }
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    void handleInsertImageFile(file);
  };

  return (
    <div className="rounded border border-blue-400 p-3">
      <div className="mb-3 flex flex-wrap gap-2">
        <ToolbarButton label="B" onClick={() => applyCommand('bold')} disabled={isBusy} />
        <ToolbarButton label="I" onClick={() => applyCommand('italic')} disabled={isBusy} />
        <ToolbarButton label="U" onClick={() => applyCommand('underline')} disabled={isBusy} />
        <ToolbarButton label="S" onClick={() => applyCommand('strikeThrough')} disabled={isBusy} />
        <ToolbarButton
          label="Список"
          onClick={() => applyCommand('insertUnorderedList')}
          disabled={isBusy}
        />
        <ToolbarButton
          label="Нумер."
          onClick={() => applyCommand('insertOrderedList')}
          disabled={isBusy}
        />
        <ToolbarButton label="Ссылка" onClick={handleCreateLink} disabled={isBusy} />
        <ToolbarButton label="Из URL" onClick={handleInsertImageByUrl} disabled={isBusy} />
        <ToolbarButton
          label="Файл"
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
        />
        <ToolbarButton
          label="Очистить"
          onClick={() => applyCommand('removeFormat')}
          disabled={isBusy}
        />
        <label className="flex items-center gap-1 text-extra-xs dark:text-white">
          <span>Цвет</span>
          <input
            type="color"
            disabled={isBusy}
            onChange={event => applyCommand('foreColor', event.target.value)}
          />
        </label>
        <label className="flex items-center gap-1 text-extra-xs dark:text-white">
          <span>Фон</span>
          <input
            type="color"
            disabled={isBusy}
            onChange={event => applyCommand('hiliteColor', event.target.value)}
          />
        </label>
        <label className="flex items-center gap-1 text-extra-xs dark:text-white">
          <span>Размер</span>
          <select
            className="rounded border border-violet-400 bg-white px-2 py-1 text-extra-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            defaultValue="3"
            disabled={isBusy}
            onChange={event => applyCommand('fontSize', event.target.value)}
          >
            <option value="1">XS</option>
            <option value="2">S</option>
            <option value="3">M</option>
            <option value="4">L</option>
            <option value="5">XL</option>
            <option value="6">2XL</option>
            <option value="7">3XL</option>
          </select>
        </label>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileInputChange}
      />

      <div className="mb-3 text-extra-xs opacity-70 dark:text-white">
        Можно вставить изображение по URL, выбрать файл или вставить картинку из буфера обмена.
      </div>

      {isEmbeddingImage && (
        <div className="mb-3 text-extra-xs text-blue-600 dark:text-blue-300">
          Подготавливаю изображение...
        </div>
      )}

      <div className="relative">
        {!plainText && (
          <div className="pointer-events-none absolute left-3 top-3 text-xs text-grey-400">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable={!isBusy}
          suppressContentEditableWarning
          className={`${minHeightClassName} rounded border border-violet-400 bg-white p-3 text-sm text-black outline-none dark:border-violet-900 dark:bg-violet-1200 dark:text-white`}
          onInput={emitChange}
          onPaste={event => {
            const clipboardItems = Array.from(event.clipboardData.items || []);
            const imageFile =
              clipboardItems
                .filter(item => item.kind === 'file')
                .map(item => item.getAsFile())
                .find(file => Boolean(file) && isSupportedRichTextImageType(file?.type)) || null;

            if (imageFile) {
              event.preventDefault();
              void handleInsertImageFile(imageFile);
              return;
            }

            event.preventDefault();
            const text = event.clipboardData.getData('text/plain');
            editorRef.current?.focus();
            withCssStyles();
            document.execCommand('insertText', false, text);
            emitChange();
          }}
        />
      </div>
    </div>
  );
};
